<template>
  <v-dialog
    v-model="canShowDialog"
    max-width="1000"
    :transition="false"
    class="aggregator-completing-data-dialog"
    @keydown.esc="closeDialog"
    persistent
  >
    <v-card
      class="v-card"
      style="max-height: 70vh; display: flex; flex-direction: column;"
      @mousemove="updateTooltipPosition"
    >
      <!--    Loader-->
      <v-overlay
        :value="showLoader"
        class="d-flex align-center justify-center"
        opacity="0.2"
        persistent
      >
        <v-progress-circular
          indeterminate
          size="60"
          color="primary"
        ></v-progress-circular>
      </v-overlay>

      <!--      Card title-->
      <v-card-title
        class="v-card-title"
      >
        {{ $t('road-maintenance/aggregator.road_closure_dialog_title') }}
        <v-spacer/>

        <div
          class="x-image"
        >
          <v-img
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @click="closeDialog"
          />
        </div>
      </v-card-title>

      <!--      Card content-->
      <v-card-text
        class="v-card-text pt-2"
      >
        <v-row
          class="roadmap-row"
        >
          <v-col
            class="roadmap-col"
            @click="onMapClick"
          >
            <google-map
              unique-id="draw-road-closure-map"
              :config="googleMapConfig"
              @map:objects="onMapObjects"
            />
          </v-col>
        </v-row>
        <v-row
          ref="delete-control"
          style="display: none; margin: 5px 0 0 0;"
        >
          <v-col
            style="padding: 0 10px 0 0;"
          >
            <universal-button
              type="base"
              prepend-icon="mdi-delete"
              style="border-radius: unset !important; width: 24px; height: 24px !important; min-width: unset !important;"
              :prepend-icon-style="{ color: 'black !important', 'font-size': '19px !important', 'margin-right': '0 !important' }"
              @click="removeDrawnPolyline"
            />
          </v-col>
        </v-row>
        <div
          v-if="isTooltipVisible"
          :style="{ top: copyIconTooltipY + 'px', left: copyIconTooltipX + 'px' }"
          class="tooltip"
        >
          {{ tooltipContent }}
        </div>
      </v-card-text>
      <v-card-actions
        style="padding: 0;"
      >
        <v-row
          style="margin: 0;"
        >
          <v-col
            class="d-flex justify-end"
          >
<!--            Cancel btn-->
            <universal-button
              type="cancel"
              style="align-self: end; font-size: 12px; margin-right: 2%;"
              @click="closeDialog"
            />

<!--            Save drawn road closure-->
            <universal-button
              type="save"
              style="align-self: end; font-size: 12px;"
              :disabled="!drawnPolyline"
              @click="saveData"
            />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import { api } from '@/global/services/api'
import GoogleMap from '@/global/components/google-map/components/GoogleMap.vue'
import store from '@/global/store'

const MINIMUM_POLYLINE_POINTS = 3
export default {
  name: 'DrawRoadRegulationOnMapDialog',

  components: {
    GoogleMap,
    UniversalButton
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: null
    },
    id: {
      type: String,
      default: null
    },
    mapEvent: {
      type: Object,
      default: null
    }
  },

  emits: [
    'close',
    'drawn-polyline'
  ],

  data () {
    return {
      canShowDialog: false,
      formData: {},
      showLoader: false,
      description: null,
      uploadedFile: null,
      googleMapConfig: {
        disablePoiClicks: true,
        controls: {
          zoomControl: true,
          mapTypeChooserControl: true,
          searchControl: true
        },
        zoom: 12
      },
      drawnPolyline: null,
      polylineToSend: [],
      isTooltipVisible: false,
      tooltipContent: '',
      copyIconTooltipX: 0,
      copyIconTooltipY: 0,
      drawingEnabled: true,
      drawingModeChanged: false,
      clickedCnt: 0
    }
  },

  watch: {
    showDialog: {
      immediate: true,
      handler (val) {
        this.canShowDialog = !!val
      }
    }
  },

  methods: {
    closeDialog () {
      this.$emit('close')
    },

    updateTooltipPosition (event) {
      this.copyIconTooltipX = event.clientX + 8
      this.copyIconTooltipY = event.clientY + 20
    },

    async saveData () {
      try {
        if (!this.polylineToSend || !this.polylineToSend.length) {
          store.dispatch('base/notifications/push', this.$t('road-maintenance/aggregator.draw_road'))
          return
        }
        else if (this.polylineToSend.length < MINIMUM_POLYLINE_POINTS) {
          store.dispatch('base/notifications/push', this.$t('road-maintenance/aggregator.min_polyline_points_warning').replace(':number', MINIMUM_POLYLINE_POINTS))
          return
        }

        this.showLoader = true
        const formData = new FormData()
        this.polylineToSend.forEach((point, index) => {
          formData.append(`polyline[${index}][lat]`, point.lat)
          formData.append(`polyline[${index}][lng]`, point.lng)
        })

        const { data } = await api('road-maintenance').post('road-activity-feed/validate-polyline', formData)

        const { polyline, street: addressStreet } = data || {}

        // Get a validated polyline, ensuring all points lie on the same street or fallback to the corrected version from the backend
        this.$emit('drawn-polyline', {
          polyline: polyline,
          street: addressStreet
        })

        this.showLoader = false
        this.closeDialog()
      }
      catch (exception) {
        console.error(exception)
        this.showLoader = false
      }
    },

    removeDrawnPolyline () {
      this.polylineToSend = []
      if (this.drawnPolyline) {
        this.drawnPolyline?.setMap(null)
        this.drawnPolyline = null
      }

      this.$refs['delete-control'].style.display = 'none'
    },

    onMapObjects (event) {
      const { map, googleMaps } = event || {}

      if (map && googleMaps) {
        let drawingManager = null
        if (googleMaps.drawing && googleMaps.drawing.DrawingManager) {
          // Define google maps drawing manager for drawing polylines
          drawingManager = new googleMaps.drawing.DrawingManager({
            drawingControl: true,
            drawingControlOptions: {
              position: googleMaps.ControlPosition.TOP_CENTER,
              drawingModes: [
                googleMaps.drawing.OverlayType.POLYLINE
              ]
            }
          })
        }

        if (drawingManager) {
          // Use it on the map
          drawingManager.setMap(map)

          // Add a delete button custom control to the map
          map.controls[googleMaps.ControlPosition.TOP_CENTER].push(this.$refs['delete-control'])
          drawingManager.setDrawingMode(googleMaps.drawing.OverlayType.POLYLINE)
          this.isTooltipVisible = true
          this.tooltipContent = this.$t('locations/map.polyline_tooltip_start')

          const that = this
          // Detect when a user finished drawing a polyline
          googleMaps.event.addListener(drawingManager, 'overlaycomplete', function (event) {
            that.isTooltipVisible = false
            that.tooltipContent = ''
            that.drawingEnabled = false
            that.tooltipContent = that.$t('locations/map.polyline_tooltip_start')
            if (event.type === googleMaps.drawing.OverlayType.POLYLINE) {
              // Show delete control for current drawn polyline
              if (that.$refs['delete-control']) {
                that.$refs['delete-control'].style.display = 'flex'
              }

              that.drawnPolyline = event.overlay
              const path = that.drawnPolyline.getPath()
              that.updatePolylineAfterEdit(googleMaps)

              // Turn off drawing mode
              drawingManager.setDrawingMode(null)

              // Listen for edit polyline points
              path.addListener('set_at', () => {
                that.updatePolylineAfterEdit(googleMaps)
              })

              path.addListener('insert_at', () => {
                that.updatePolylineAfterEdit(googleMaps)
              })

              path.addListener('remove_at', () => {
                that.updatePolylineAfterEdit(googleMaps)
              })
            }
          })

          // Detect when a user clicks on polyline drawing control to remove a previously added polyline
          googleMaps.event.addListener(drawingManager, 'drawingmode_changed', () => {
            const mode = drawingManager.getDrawingMode()
            that.drawingModeChanged = true

            if (mode === googleMaps.drawing.OverlayType.POLYLINE) {
              that.tooltipContent = that.$t('locations/map.polyline_tooltip_start')
              that.isTooltipVisible = true
              that.clickedCnt = 0
              if (that.drawnPolyline) {
                that.drawnPolyline.setMap(null)
                that.drawnPolyline = null
              }
            }
            else {
              that.tooltipContent = ''
              that.clickedCnt = 0
            }

            that.isTooltipVisible = mode === googleMaps.drawing.OverlayType.POLYLINE
            that.drawingEnabled = mode === googleMaps.drawing.OverlayType.POLYLINE
          })
        }
      }
    },

    onMapClick () {
      if (this.drawingEnabled && !this.drawingModeChanged) {
        this.clickedCnt++

        if (this.clickedCnt === 1) {
          this.tooltipContent = this.$t('locations/map.polyline_tooltip_cont')
        }
        else if (this.clickedCnt > 1) {
          this.tooltipContent = this.$t('locations/map.polyline_tooltip_end')
        }
        else {
          this.tooltipContent = this.$t('locations/map.polyline_tooltip_start')
        }
      }

      if (this.drawingModeChanged) {
        this.drawingModeChanged = false
      }
    },

    // Update polyline with arrows that showing polyline direction
    updatePolylineAfterEdit (googleMaps) {
      const path = this.drawnPolyline.getPath().getArray()

      this.polylineToSend = path
        .map(point => ({
          lat: point.lat(),
          lng: point.lng()
        }))

      const icons = []
      for (let i = 0; i < path.length - 1; i++) {
        const offset = ((i + 0.5) / (path.length - 1)) * 100
        icons.push({
          icon: {
            path: googleMaps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 2,
            strokeColor: 'red'
          },
          offset: `${offset}%`
        })
      }

      // Set polyline arrows and make it editable
      this.drawnPolyline.setOptions({
        icons: icons,
        editable: true
      })
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep.v-dialog__content .v-dialog {
  border-radius: 16px !important;
}

.v-card {
  max-width: 1000px;
  border-radius: 16px;
}

.v-card-text {
  background-color: #FFFFFF;
  overflow-y: hidden;
  height: 880px;
  padding: 0 !important;
}

.roadmap-col {
  height: 100%;
  padding: 0 !important;
}

.roadmap-row {
  height: 100%;
  margin: 0 !important;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 24px !important;
  line-height: 32px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.x-image:hover {
  cursor: pointer;
}

::v-deep {
  .gmnoprint[role="menubar"] > div > button {
  }
}
</style>
